/**
 * Read-only displays shared by the entry dialog and the detail dialog (batch 8 T12c3, #3033): the matcher's evaluation, the live
 * coverage of the order, and the unresolved writes of the owner. Everything shown is what the server answered.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import type {
  CoverageView,
  EvaluationView,
  FactsLine,
  InvoiceOperations,
  OperationResult,
} from '@/hooks/useSupplierInvoices';
import type { JournalOperation } from './invoiceJournal';
import { LineStatusChip, NS, type ErrorText, StatusChip, ViolationList, poLineLabel, useCodeText, useErrorText, useFormatters } from './invoiceView';

// ---------------------------------------------------------------------------
// Evaluation of one document
// ---------------------------------------------------------------------------

export function EvaluationView_({ evaluation, poLines }: { evaluation: EvaluationView; poLines: readonly FactsLine[] }) {
  const { t } = useTranslation(NS);
  const fmt = useFormatters();
  const text = useCodeText();
  if (!evaluation.ok) {
    return (
      <Alert severity="error" data-testid="evaluation-violations">
        <AlertTitle>{t('evaluation.notEvaluable')}</AlertTitle>
        <ViolationList violations={evaluation.violations} />
      </Alert>
    );
  }
  const { result } = evaluation;
  const byId = new Map(poLines.map((line) => [line.id.toLowerCase(), line] as const));
  return (
    <Stack spacing={1.5} data-testid="evaluation">
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        <Typography variant="subtitle2">{t('evaluation.proposedStatus')}</Typography>
        <StatusChip status={result.proposedStatus} />
        <Chip size="small" variant="outlined" label={t('evaluation.noStockEffect')} />
      </Stack>
      {result.vatUnknown && <Alert severity="warning">{t('evaluation.vatUnknown')}</Alert>}
      {result.unsupportedLegacyQuantity > 0 && (
        <Alert severity="warning">{t('evaluation.legacyUnsupported', { count: result.unsupportedLegacyQuantity })}</Alert>
      )}
      {!result.arithmetic.ok && (
        <Alert severity="error" data-testid="arithmetic-issues">
          <AlertTitle>{t('evaluation.arithmeticTitle')}</AlertTitle>
          <Typography variant="body2" gutterBottom>
            {t('evaluation.arithmeticNotOverridable')}
          </Typography>
          <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
            {result.arithmetic.issues.map((issue, index) => (
              <li key={`${issue.code}-${String(index)}`}>
                {text('arithmeticIssue', issue.code)}
                {issue.lineNumber === undefined ? '' : ` (${t('violationLine', { number: issue.lineNumber })})`}
                {': '}
                {t('evaluation.printedVsCalculated', { printed: fmt.cents(issue.statedCents), calculated: fmt.cents(issue.expectedCents) })}
              </li>
            ))}
          </ul>
        </Alert>
      )}
      <Typography variant="body2" color="text.secondary">
        {t('evaluation.calculatedByServer', {
          net: fmt.cents(result.arithmetic.expected.netCents),
          vat: fmt.cents(result.arithmetic.expected.vatCents),
          gross: fmt.cents(result.arithmetic.expected.grossCents),
        })}
        {result.arithmetic.lineRoundingDifferenceCents !== 0 &&
          ` ${t('evaluation.roundingDifference', { amount: fmt.cents(result.arithmetic.lineRoundingDifferenceCents) })}`}
      </Typography>
      <TableContainer>
        <Table size="small" aria-label={t('evaluation.linesAria')}>
          <TableHead>
            <TableRow>
              <TableCell>{t('evaluation.columns.line')}</TableCell>
              <TableCell>{t('evaluation.columns.poLine')}</TableCell>
              <TableCell align="right">{t('evaluation.columns.quantity')}</TableCell>
              <TableCell align="right">{t('evaluation.columns.net')}</TableCell>
              <TableCell>{t('evaluation.columns.status')}</TableCell>
              <TableCell align="right">{t('evaluation.columns.variance')}</TableCell>
              <TableCell>{t('evaluation.columns.reasons')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {result.lines.map((line) => (
              <TableRow key={line.lineNumber} data-testid={`evaluation-line-${String(line.lineNumber)}`}>
                <TableCell>{line.lineNumber}</TableCell>
                <TableCell>{poLineLabel(line.purchaseOrderLineId ? byId.get(line.purchaseOrderLineId.toLowerCase()) : undefined, t)}</TableCell>
                <TableCell align="right">{line.quantity}</TableCell>
                <TableCell align="right">{fmt.cents(line.lineNetCents)}</TableCell>
                <TableCell>
                  <LineStatusChip status={line.matchStatus} />
                </TableCell>
                <TableCell align="right">
                  {line.varianceCents !== null && <div>{fmt.cents(line.varianceCents)}</div>}
                  {line.varianceQuantity !== null && line.varianceQuantity !== 0 && (
                    <div>{t('evaluation.varianceQuantity', { count: line.varianceQuantity })}</div>
                  )}
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    {line.reasons.map((reason, index) => (
                      <Chip
                        key={`${reason.code}-${String(index)}`}
                        size="small"
                        variant="outlined"
                        label={
                          reason.quantity === undefined
                            ? text('reason', reason.code)
                            : `${text('reason', reason.code)} (${String(reason.quantity)})`
                        }
                      />
                    ))}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Coverage of the order over ALL documents
// ---------------------------------------------------------------------------

const STATE_COLOR: Record<string, 'default' | 'success' | 'warning' | 'error'> = {
  nothing_accepted: 'default',
  uncovered: 'warning',
  partially_covered: 'warning',
  covered: 'success',
  legacy_unsupported: 'error',
};

export function CoverageSummary({
  coverage,
  compareWith,
  poLines,
  compareLabels,
}: {
  coverage: CoverageView;
  compareWith?: CoverageView | null;
  poLines: readonly FactsLine[];
  compareLabels?: { current: string; other: string };
}) {
  const { t } = useTranslation(NS);
  const text = useCodeText();
  if (!coverage.ok) {
    return (
      <Alert severity="warning" data-testid="coverage-unavailable">
        <AlertTitle>{t('coverage.unavailable')}</AlertTitle>
        <ViolationList violations={coverage.violations} />
      </Alert>
    );
  }
  const { result } = coverage;
  const other = compareWith && compareWith.ok ? compareWith.result : null;
  const otherById = new Map((other?.lines ?? []).map((line) => [line.purchaseOrderLineId.toLowerCase(), line] as const));
  const lineById = new Map(poLines.map((line) => [line.id.toLowerCase(), line] as const));
  return (
    <Stack spacing={1} data-testid="coverage">
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Chip size="small" color={result.coverageComplete ? 'success' : 'warning'} label={result.coverageComplete ? t('coverage.complete') : t('coverage.incomplete')} />
        <Chip size="small" color={result.fullyMatched ? 'success' : 'default'} label={result.fullyMatched ? t('coverage.fullyMatched') : t('coverage.notFullyMatched')} />
        {result.awaitingFurtherDelivery && <Chip size="small" variant="outlined" label={t('coverage.awaitingDelivery')} />}
        {result.unsupportedLegacyQuantity > 0 && (
          <Chip size="small" color="error" label={t('coverage.legacyUnsupported', { count: result.unsupportedLegacyQuantity })} />
        )}
      </Stack>
      <TableContainer>
        <Table size="small" aria-label={t('coverage.aria')}>
          <TableHead>
            <TableRow>
              <TableCell>{t('coverage.columns.poLine')}</TableCell>
              <TableCell>{t('coverage.columns.state')}</TableCell>
              <TableCell align="right">{t('coverage.columns.accepted')}</TableCell>
              <TableCell align="right">{t('coverage.columns.covered')}</TableCell>
              <TableCell align="right">{t('coverage.columns.reserved')}</TableCell>
              <TableCell align="right">{t('coverage.columns.unbilled')}</TableCell>
              <TableCell align="right">{t('coverage.columns.openCredit')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {result.lines.map((line) => {
              const id = line.purchaseOrderLineId.toLowerCase();
              const after = otherById.get(id);
              return (
                <TableRow key={id} data-testid={`coverage-line-${id}`}>
                  <TableCell>{poLineLabel(lineById.get(id), t)}</TableCell>
                  <TableCell>
                    <Chip size="small" color={STATE_COLOR[line.state] ?? 'default'} label={text('coverageState', line.state)} />
                    {after && after.state !== line.state && (
                      <Chip
                        size="small"
                        sx={{ ml: 0.5 }}
                        color={STATE_COLOR[after.state] ?? 'default'}
                        label={`${compareLabels?.other ?? ''} ${text('coverageState', after.state)}`.trim()}
                      />
                    )}
                  </TableCell>
                  <TableCell align="right">{line.receiptBackedAccepted}</TableCell>
                  <TableCell align="right">
                    {line.allocatedSettled}
                    {after && after.allocatedSettled !== line.allocatedSettled && ` → ${String(after.allocatedSettled)}`}
                  </TableCell>
                  <TableCell align="right">
                    {line.allocatedLive}
                    {after && after.allocatedLive !== line.allocatedLive && ` → ${String(after.allocatedLive)}`}
                  </TableCell>
                  <TableCell align="right">
                    {line.unbilledAccepted}
                    {after && after.unbilledAccepted !== line.unbilledAccepted && ` → ${String(after.unbilledAccepted)}`}
                  </TableCell>
                  <TableCell align="right">{line.openCreditUnits}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      {compareLabels && <Typography variant="caption" color="text.secondary">{t('coverage.compareNote', { current: compareLabels.current, other: compareLabels.other })}</Typography>}
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Unresolved writes of this owner and order
// ---------------------------------------------------------------------------

/**
 * What the screen last learnt about one explicit retry (or discard) of a held request. It belongs to the screen that shows the held
 * requests, not to the request's alert: a request the server answered for good leaves the journal, and its alert with it, but the
 * answer must stay in view until the user has read it. `key` is the Idempotency-Key of the request the answer is about, so an
 * answer is never shown for another request of the same slot. Errors are kept raw and translated when shown (language follows).
 */
interface RetryNote {
  readonly key: string;
  readonly tone: 'success' | 'refused' | 'uncertain' | 'error';
  readonly operation: Pick<JournalOperation, 'kind' | 'slot' | 'label' | 'key'>;
  readonly error?: unknown;
  readonly blockedReason?: string;
}

export function PendingOperations({ ops, onResult }: { ops: InvoiceOperations; onResult?: (result: OperationResult, operation: JournalOperation) => void }) {
  const { t } = useTranslation(NS);
  const { t: tc } = useTranslation('common');
  const errorText = useErrorText();
  // Lives as long as this screen: it is mounted inside the owner/target boundary, so another owner, session, rights change or
  // another document starts with no notes, and no note of an old owner can be shown to a new one.
  const [notes, setNotes] = useState<Record<string, RetryNote>>({});
  const [discarding, setDiscarding] = useState<JournalOperation | null>(null);
  const [discardingDamaged, setDiscardingDamaged] = useState(false);
  const [restoreFailed, setRestoreFailed] = useState(false);

  const restore = () => setRestoreFailed(!ops.restore());

  const note = (operation: JournalOperation, tone: RetryNote['tone'], extra: { error?: unknown; blockedReason?: string } = {}) =>
    setNotes((current) => ({
      ...current,
      [operation.slot]: {
        key: operation.key,
        tone,
        operation: { kind: operation.kind, slot: operation.slot, label: operation.label, key: operation.key },
        ...extra,
      },
    }));

  const dismiss = (slot: string) =>
    setNotes((current) => {
      const { [slot]: _gone, ...rest } = current;
      return rest;
    });

  // A new request in a slot (another key) supersedes the answer about the earlier one: that answer is no longer about what is held.
  useEffect(() => {
    setNotes((current) => {
      let next = current;
      for (const operation of ops.operations) {
        const held = next[operation.slot];
        if (held && held.key !== operation.key) {
          const { [operation.slot]: _gone, ...rest } = next;
          next = rest;
        }
      }
      return next;
    });
  }, [ops.operations]);

  const retry = async (operation: JournalOperation) => {
    const result = await ops.retry(operation);
    if (result.status === 'success') note(operation, 'success');
    else if (result.status === 'refused') note(operation, 'refused', { error: result.error });
    else if (result.status === 'uncertain') note(operation, 'uncertain', { error: result.error });
    else if (result.status === 'blocked') note(operation, 'error', { blockedReason: result.reason });
    else if (result.error !== undefined) note(operation, 'error', { error: result.error });
    onResult?.(result, operation);
  };

  const heldKeys = new Set(ops.operations.map((operation) => `${operation.slot}|${operation.key}`));
  // Answers about requests that are no longer held (stored, or refused for good): only these cannot live inside a held request's alert.
  const resolved = Object.values(notes).filter(
    (entry) => (entry.tone === 'success' || entry.tone === 'refused') && !heldKeys.has(`${entry.operation.slot}|${entry.key}`),
  );

  const noteBody = (entry: RetryNote) => {
    if (entry.tone === 'success') return t('pending.done');
    if (entry.blockedReason !== undefined) return t(`blocked.${entry.blockedReason}`);
    const text = entry.error === undefined ? null : errorText(entry.error);
    if (entry.tone === 'uncertain') return `${t('pending.stillUnknown')} ${text?.message ?? ''}`.trim();
    return text?.message ?? '';
  };

  return (
    <Stack spacing={1} data-testid="pending-operations">
      {ops.journalStatus === 'corrupt' && (
        <Alert
          severity="error"
          action={
            ops.restorable ? (
              <Button color="inherit" size="small" onClick={restore}>
                {t('journal.restore')}
              </Button>
            ) : (
              <Button color="inherit" size="small" onClick={() => setDiscardingDamaged(true)}>
                {t('journal.discardDamaged')}
              </Button>
            )
          }
        >
          <AlertTitle>{t('journal.corruptTitle')}</AlertTitle>
          {ops.restorable ? t('journal.corruptRestorableBody') : t('journal.corruptBody')}
        </Alert>
      )}
      {ops.journalStatus === 'lost' && (
        <Alert
          severity="error"
          data-testid="journal-lost"
          action={
            <Button color="inherit" size="small" onClick={restore}>
              {t('journal.restore')}
            </Button>
          }
        >
          <AlertTitle>{t('journal.lostTitle')}</AlertTitle>
          {t('journal.lostBody')}
          <Typography variant="body2">{t('journal.restoreNote')}</Typography>
        </Alert>
      )}
      {restoreFailed && ops.restorable && (
        <Alert severity="warning" role="status">
          {t('journal.restoreFailed')}
        </Alert>
      )}
      {ops.journalStatus === 'unavailable' && (
        <Alert severity="error">
          <AlertTitle>{t('journal.unavailableTitle')}</AlertTitle>
          {t('journal.unavailableBody')}
        </Alert>
      )}
      {ops.operations.map((operation) => {
        const sending = ops.inFlight.has(operation.slot);
        const saved = notes[operation.slot]?.key === operation.key ? notes[operation.slot] : undefined;
        return (
          <Alert key={operation.slot} severity="warning" data-testid={`pending-${operation.slot}`}>
            <AlertTitle>{t(`pending.title.${operation.kind}`, { number: operation.label })}</AlertTitle>
            <Typography variant="body2">{operation.attempts === 0 ? t('pending.notSent') : t('pending.unknown', { count: operation.attempts })}</Typography>
            <Typography variant="body2">{t('pending.sameRequest')}</Typography>
            {saved && (
              <Alert severity={saved.tone === 'uncertain' ? 'warning' : saved.tone === 'success' ? 'success' : 'error'} sx={{ mt: 1 }} role="status">
                {noteBody(saved)}
              </Alert>
            )}
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <Button size="small" variant="outlined" disabled={sending} onClick={() => void retry(operation)}>
                {sending ? tc('loading') : t('pending.retry')}
              </Button>
              <Button size="small" color="inherit" disabled={sending} onClick={() => setDiscarding(operation)}>
                {t('pending.discard')}
              </Button>
            </Stack>
          </Alert>
        );
      })}
      {resolved.map((entry) => (
        <Alert
          key={`${entry.operation.slot}|${entry.key}`}
          severity={entry.tone === 'success' ? 'success' : 'error'}
          role="status"
          data-testid={`pending-result-${entry.operation.slot}`}
          action={
            <Button color="inherit" size="small" onClick={() => dismiss(entry.operation.slot)}>
              {tc('close')}
            </Button>
          }
        >
          {entry.tone === 'success' ? (
            <>
              <AlertTitle>{t(`pending.doneTitle.${entry.operation.kind}`, { number: entry.operation.label })}</AlertTitle>
              {t('pending.done')}
            </>
          ) : (
            <RefusedNote entry={entry} text={entry.error === undefined ? null : errorText(entry.error)} />
          )}
        </Alert>
      ))}
      <ConfirmDialog
        open={discarding !== null}
        title={t('pending.discardTitle')}
        message={t('pending.discardMessage')}
        confirmText={t('pending.discardConfirm')}
        confirmColor="error"
        onCancel={() => setDiscarding(null)}
        onConfirm={() => {
          if (discarding) {
            const removed = ops.discard(discarding);
            if (!removed) note(discarding, 'error', { blockedReason: 'storage' });
            else dismiss(discarding.slot);
          }
          setDiscarding(null);
        }}
      />
      <ConfirmDialog
        open={discardingDamaged}
        title={t('journal.discardDamagedTitle')}
        message={t('journal.discardDamagedMessage')}
        confirmText={t('journal.discardDamagedConfirm')}
        confirmColor="error"
        onCancel={() => setDiscardingDamaged(false)}
        onConfirm={() => {
          ops.discardDamaged();
          setDiscardingDamaged(false);
        }}
      />
    </Stack>
  );
}

function RefusedNote({ entry, text }: { entry: RetryNote; text: ErrorText | null }) {
  const { t } = useTranslation(NS);
  return (
    <>
      <AlertTitle>{t('pending.refusedTitle')}</AlertTitle>
      <Typography variant="body2">{t(`pending.refusedFor.${entry.operation.kind}`, { number: entry.operation.label })}</Typography>
      {text && <Typography variant="body2">{text.message}</Typography>}
      {text && text.violations.length > 0 && <ViolationList violations={text.violations} />}
      <Typography variant="body2">{t('pending.refusedBody')}</Typography>
    </>
  );
}

export function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box component="section" aria-label={title} sx={{ mb: 2 }}>
      <Typography variant="subtitle1" component="h3" gutterBottom>
        {title}
      </Typography>
      {children}
    </Box>
  );
}
